
import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

interface Resource {
  id: string
  title: string
  description: string | null
  module: string | null
  domain: string | null
  priority: boolean
  file_url: string | null
  created_at: string
  source: 'admin' | 'school'
  type?: string
}

export const useResources = () => {
  const [resources, setResources] = useState<Resource[]>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  const fetchResources = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      let allResources: Resource[] = []

      // Fetch global admin resources (school_id is null)
      const { data: globalAdminResources, error: globalError } = await supabase
        .from('admin_resources')
        .select('*')
        .eq('type', 'discover')
        .is('school_id', null)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false })

      if (globalError) throw globalError
      allResources = [...(globalAdminResources || []).map(resource => ({ ...resource, source: 'admin' as const }))]

      // Fetch school-specific admin resources if user is logged in
      if (session) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('school_id')
          .eq('id', session.user.id)
          .single()

        if (profile?.school_id) {
          // Fetch school-specific admin resources
          const { data: schoolAdminResources, error: schoolAdminError } = await supabase
            .from('admin_resources')
            .select('*')
            .eq('type', 'discover')
            .eq('school_id', profile.school_id)
            .order('priority', { ascending: false })
            .order('created_at', { ascending: false })

          if (schoolAdminError) throw schoolAdminError
          allResources = [...allResources, ...(schoolAdminResources || []).map(resource => ({ ...resource, source: 'admin' as const }))]

          // Fetch school operator resources
          const { data: schoolResourcesData, error: schoolError } = await supabase
            .from('school_resources')
            .select('*')
            .eq('school_id', profile.school_id)
            .order('priority', { ascending: false })
            .order('created_at', { ascending: false })

          if (schoolError) throw schoolError
          allResources = [...allResources, ...(schoolResourcesData || []).map(resource => ({ ...resource, source: 'school' as const }))]
        }
      }

      setResources(allResources)
    } catch (error: any) {
      console.error('Error fetching resources:', error)
      toast({
        title: "Error",
        description: "Failed to load resources",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchResources()

    // Set up real-time subscriptions
    const adminChannel = supabase
      .channel('admin-resources-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'admin_resources'
        },
        () => {
          fetchResources()
        }
      )
      .subscribe()

    const schoolChannel = supabase
      .channel('school-resources-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'school_resources'
        },
        () => {
          fetchResources()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(adminChannel)
      supabase.removeChannel(schoolChannel)
    }
  }, [])

  return {
    resources,
    loading,
    refreshResources: fetchResources
  }
}
